import {
  Component,
  ViewEncapsulation,
  OnInit,
  Inject, OnDestroy
} from '@angular/core';

import {
  DOCUMENT
} from '@angular/common';
import * as moment from 'moment';
import {
  ApiService
} from '../../services/api';
import {HttpParams} from '@angular/common/http';
import {UserContext} from '../../services/user-context';

@Component({
  selector: 'app-distributor-dashboard',
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'distributor-dashboard.template.html',
  styleUrls: [
    './distributor-dashboard.style.scss'
  ]
})
export class DistributorDashboardComponent implements OnInit, OnDestroy {
  start: Date = moment().subtract(30, 'days').toDate();
  end: Date = moment().toDate();
  locations = 0;
  lat = 35.268107;
  lng = -90.744821;
  zoom = 4;

  public ranges = [
    {range: 'day', title: 'Today'},
    {range: 'wtd', title: 'This Week'},
    {range: 'mtd', title: 'This Month'},
    {range: 'qtd', title: 'This Quarter'},
    {range: 'ytd', title: 'This Year'},
    {range: 'week', title: 'Last 7 Days'},
    {range: 'month', title: 'Last 30 Days'},
    {range: 'quarter', title: 'Last 3 Months'},
    {range: 'year', title: 'Last 12 Months'}
  ];

  public mapStyles = [
    {elementType: 'geometry', stylers: [{color: '#aec3cb'}]},
    {elementType: 'labels.text.stroke', stylers: [{'visibility': 'off'}]},
    {elementType: 'labels.text.fill', stylers: [{color: '#888888'}]},
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{color: '#555555'}]
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [{'visibility': 'off'}]
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{color: 'transparent'}]
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [{color: 'transparent'}]
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{color: '#c0d7e0'}]
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{color: '#bbdefb'}]
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [{color: '#9ca5b3'}]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [{color: '#c0d7e0'}]
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [{color: '#c0d7e0'}]
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{color: '#81d4fa'}]
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [{color: '#2f3948'}]
    },
    {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [{color: 'transparent'}]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{color: '#f1f4f5'}]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [{color: 'transparent'}]
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [{color: 'transparent'}]
    }
  ];

  public summary = {revenue: '0', sales: '0', net: '0', cost: '0', processed: '0', conversion: '0', average: '0'};
  public revenue = [];
  public revenueSeries: any[] = [];
  public commissionSeries: any[] = [];
  public volumeSeries: any[] = [];
  public averageSeries: any[] = [];

  private series: any[] = [];
  private categories: string[] = [];
  private markers: any[] = [];

  constructor(@Inject(DOCUMENT) private document: Document, private api: ApiService, public userContext: UserContext) {

    this.refreshSummary();
    this.refreshSalesTrend();
    this.refreshMap();
  }

  public filterFieldChanged(): void {
    this.refreshSummary();
    this.refreshMap();
  }

  public labelContent(e: any): string {
    return e.category;
  }

  public ngOnInit(): void {
    this.document.body.style.backgroundColor = '#f1f4f5';
    const mainLayout = this.document.getElementsByTagName('app-main-layout');
    mainLayout[0].classList.add('white-header');
  }

  public ngOnDestroy(): void {
    this.document.body.style.backgroundColor = '#ffffff';
    const mainLayout = this.document.getElementsByTagName('app-main-layout');
    mainLayout[0].classList.remove('white-header');
  }

  public refreshSummary(): void {
    this.api.get<any>(`/summary`, new HttpParams()
      .append('start', moment(this.start).toISOString())
      .append('end', moment(this.end).toISOString()))
      .subscribe(result => {
        if ( Array.isArray(result.summary) && result.summary.length) {
          this.summary = result.summary[0];
        }

        this.revenueSeries = [];
        this.commissionSeries = [];
        this.volumeSeries = [];
        this.averageSeries = [];

        result.trend.map((day) => {
          this.revenueSeries.push(day.revenue || 0);
          this.commissionSeries.push(day.commission || 0);
          this.volumeSeries.push(day.sales || 0);
          this.averageSeries.push(day.average || 0);
        });

      });
  }

  public refreshMap(): void {
    this.api.get<any>(`/sales/map`, new HttpParams()
      .append('start', moment(this.start).toISOString())
      .append('end', moment(this.end).toISOString()))
      .subscribe(result => {
        this.markers = result.filter((marker) => marker.latitude && marker.longitude);
      });
  }

  public refreshSalesTrend(): void {
    this.api.get<any>(`/sales`)
      .subscribe(result => {
        const dates: string[] = [];
        const sales: any = {};
        const services = new Set();
        const totals = {};
        const series: any[] = [];

        result.map((day) => {
          dates.push(day._id);
          day.services.map((service) => {
            services.add(service.type);
          });
        });

        services.forEach((service:string) => {
          sales[service] = {data: []};
        });

        result.map((day) => {
          const serviceSet = new Set();
          day.services.map((service) => {
            serviceSet.add(service.type);
            sales[service.type].data.push(service.quantity || 0);
          });

          [...Array.from(services)].filter(x => !serviceSet.has(x)).map((emptyService:string) => {
            sales[emptyService].data.push(0);
          });
        });

        Object.keys(sales).forEach((key) => {
          totals[key] = sales[key].data.reduce( (a, b) => {
            return a + b;
          }, 0);
        });

        Object.keys(sales).forEach((service) => {
          series.push({name: service, data: sales[service].data});
        });
        this.categories = dates;
        this.series = series;
      });
  }
}
